import { RouteData } from "shared/config/routeData";
import { clientTaskIds, policyTaskIds } from "shared/data/Tasks";
import {
  getIsCoverageSupportedInEIF,
  notAllowedPreviousCoveragesBenefits,
} from "shared/types/SlfCoverages";
import { getAreTherePendingChanges, getWereCoveragesChanged } from "shared/utils/EIF/changeLog";
import { assertIsDefined } from "shared/utils/utils";
import type { ValidParam } from "shared/config/routeData";
import type { TaskId, ClientTaskId, PolicyTaskId } from "shared/data/Tasks";
import type { DEIFChangeSnapshot } from "shared/types/Change";
import type {
  Client,
  ClientId,
  EIFSubStepViewMode,
  Policy,
  PolicyId,
  EAPIsBlendedWith,
} from "shared/types/Client";
import type { EIFStepId, EIFSubStepId } from "shared/types/EIF";
import type { CoverageSupportedInEIF } from "shared/types/SlfCoverages";

export const isTaskId = (taskId: TaskId | undefined): taskId is TaskId => {
  return isClientTaskId(taskId) || isPolicyTaskId(taskId);
};

export const getTaskDetailRoute = (
  clientId: ClientId,
  taskId: TaskId,
  policyId: PolicyId | undefined,
) => {
  if (isPolicyTaskId(taskId) && policyId) {
    return RouteData.policyTaskDetail.getPath(clientId, taskId, policyId);
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return RouteData.clientTaskDetail.getPath(clientId, taskId as ClientTaskId);
};

export const isClientTaskId = (taskId: TaskId | ValidParam | undefined): taskId is ClientTaskId =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  Boolean(taskId) && clientTaskIds.includes(taskId as ClientTaskId);

export const isPolicyTaskId = (taskId: TaskId | ValidParam | undefined): taskId is PolicyTaskId =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  Boolean(taskId) && policyTaskIds.includes(taskId as PolicyTaskId);

export const getEIFTaskAndStepDetailRoute = (
  clientId: ClientId,
  stepId: EIFStepId,
  subStepId: EIFSubStepId,
) => {
  return RouteData.eifSubStepDetail.getPath(clientId, stepId, subStepId);
};

export const trimListOfEmails = (listOfEmails: string | null) => {
  if (!listOfEmails) {
    return undefined;
  }
  return listOfEmails
    .trim()
    .split(/[,;\s]+/)
    .map((email) => email.trim())
    .join();
};

export const isAddCoverageClient = (client: Pick<Client, "implementationType">): boolean =>
  client.implementationType === "ADD_COVERAGE";

export const getPrimaryPolicy = <
  T extends { policies: K[] },
  K extends { primaryPolicy: boolean } = Policy,
>(
  client: T,
): K => {
  const primaryPolicy = client.policies.find((p) => p.primaryPolicy);
  assertIsDefined(primaryPolicy, "primaryPolicy");
  return primaryPolicy;
};

export const getPrimaryPolicyId = (client: Client) => {
  const primaryPolicy = getPrimaryPolicy(client);
  return primaryPolicy.id;
};

export function getIsMultiPolicyMode(client: Client) {
  const isMultiPolicyMode = client.policies.length > 1;
  return isMultiPolicyMode;
}

export const getShowValidationErrorsInSummary = (
  client: Client,
  viewMode: EIFSubStepViewMode,
  changeSnapshot: DEIFChangeSnapshot | null,
) => {
  const coveragesChanged = getWereCoveragesChanged(client, changeSnapshot);
  if (coveragesChanged) return true;

  const areTherePendingChanges =
    changeSnapshot === null ? false : getAreTherePendingChanges({ client, changeSnapshot });

  const showValidationErrorsInSummary =
    (viewMode === "post-sign-with-edit" || viewMode === "review-page") && !areTherePendingChanges
      ? false
      : true;

  return showValidationErrorsInSummary;
};

export function getEIFSubStepViewMode(args: { client: Client }): EIFSubStepViewMode {
  const { client } = args;

  if (client.eifSignedAt) {
    return "post-sign-with-edit";
  }

  return "pre-sign";
}

export function getAllowedPreviousCoverages(client: Client): CoverageSupportedInEIF[] {
  const clientEIFSupportedSlfCoverages = (client.allPoliciesSlfCoverages ?? [])
    .filter(getIsCoverageSupportedInEIF)
    .filter((coverage) => !notAllowedPreviousCoveragesBenefits.includes(coverage));

  return clientEIFSupportedSlfCoverages;
}

export function getEAPShouldBeHidden(eapIsBlendedWith: EAPIsBlendedWith | null) {
  // https://sunlife.atlassian.net/browse/TB-7127
  const eapShouldBeHidden = new Array<typeof eapIsBlendedWith>("LIFE", "STD", "LTD").includes(
    eapIsBlendedWith,
  );
  return eapShouldBeHidden;
}
